import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const QuizResults = ({ results, onGetStarted, onBookCall }) => {
  const getScoreColor = (score) => {
    if (score >= 71) return 'text-teal';
    if (score >= 41) return 'text-bronze';
    return 'text-graphite';
  };

  const getScoreLabel = (score) => {
    if (score >= 71) return 'CEO Ready';
    if (score >= 41) return 'Scale Ready';
    return 'Foundation Building';
  };

  return (
    <div className="min-h-screen bg-ivory">
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="headline-medium text-charcoal mb-6">
              Your Founder Profile: {results.founder_profile || results.icp_profile?.name}
            </h1>
            <p className="body-large text-graphite mb-8 max-w-3xl mx-auto">
              {results.description || results.icp_profile?.description}
            </p>
            
            {/* Score Display */}
            <div className="mb-8">
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.score || results.total_score)}`}>
                {results.score || results.total_score}/100
              </div>
              <div className={`subhead-large ${getScoreColor(results.score || results.total_score)}`}>
                {getScoreLabel(results.score || results.total_score)}
              </div>
            </div>

            {/* Score Visualization */}
            <div className="max-w-md mx-auto mb-8">
              <div className="progress-bar h-3 bg-gray-200">
                <div 
                  className="progress-fill h-full bg-teal rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${results.score || results.total_score}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-graphite mt-2">
                <span>Solo Operator</span>
                <span>CEO Ready</span>
              </div>
            </div>
          </div>

          {/* Results Grid */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Strengths */}
            <Card className="editorial-card hover-lift">
              <h3 className="headline-small text-charcoal mb-6">Where You're Strong</h3>
              <ul className="space-y-4">
                {(results.strengths || results.icp_profile?.strengths || []).map((strength, index) => (
                  <li key={index} className="body-medium flex items-start">
                    <span className="text-teal mr-3 text-xl">✓</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Build Areas */}
            <Card className="editorial-card hover-lift">
              <h3 className="headline-small text-charcoal mb-6">Where We'll Build</h3>
              <ul className="space-y-4">
                {(results.build_areas || results.icp_profile?.build_areas || []).map((area, index) => (
                  <li key={index} className="body-medium flex items-start">
                    <span className="text-bronze mr-3 text-xl">→</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Personalized Recommendations */}
          <Card className="editorial-card mb-16">
            <h3 className="headline-small text-charcoal mb-8">Personalized Recommendations</h3>
            <div className="space-y-6">
              {(results.recommendations || []).map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-bronze text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    {index + 1}
                  </div>
                  <p className="body-medium text-charcoal">{recommendation}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Next Steps */}
          <Card className="editorial-card text-center">
            <h3 className="headline-small text-charcoal mb-8">Recommended Next Steps</h3>
            
            <div className="space-y-4 md:space-y-0 md:flex md:space-x-6 justify-center">
              <Button 
                className="btn-primary text-lg px-12 py-4" 
                onClick={onBookCall}
              >
                Review Your Results on a Strategy Call
              </Button>
              <Button 
                variant="outline" 
                className="btn-secondary text-lg px-12 py-4" 
                onClick={onGetStarted}
              >
                Enroll in the 10 Week Build Program
              </Button>
            </div>

            {/* Optional Enhancement - Score Breakdown */}
            <div className="mt-12 pt-8 border-t border-bronze-light">
              <p className="label-text text-graphite mb-4">YOUR ASSESSMENT BREAKDOWN</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-bronze mb-1">Business</div>
                  <div className="text-sm text-graphite">Foundation</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-bronze mb-1">Brand</div>
                  <div className="text-sm text-graphite">Presence</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-bronze mb-1">Systems</div>
                  <div className="text-sm text-graphite">Operations</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-bronze mb-1">Growth</div>
                  <div className="text-sm text-graphite">Readiness</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Optional: Email Report CTA */}
          <div className="text-center mt-12">
            <p className="body-medium text-graphite mb-4">
              Want your complete personalized report emailed to you?
            </p>
            <Button variant="outline" className="btn-secondary">
              Email My Full Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;